import React, { useContext, useMemo } from "react";
import { useFragment } from "react-relay";
import { useLocation } from "react-router";

import { faSparkles } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { throttle } from "lodash";
import moment from "moment";
import useResizeObserver from "use-resize-observer";

import { BillingContext } from "~/account/Billing/BillingContext";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { withForwardedRefSuspense } from "~/lib/suspense";
import type { ChaptersPanelRef } from "~/meetingRecorder/ChaptersPanel";
import { openClipModal } from "~/meetingRecorder/ClipModal";
import { RECORDING_VIDEO_PLAYER_READY, RECORDING_VIDEO_START_PLAYING } from "~/meetingRecorder/constants";
import {
	openTranscriptModal,
	TranscriptModalContent,
	type TranscriptModalContentRef,
	transcriptModalViewerFragment,
	type TranscriptMode,
	transcriptNoteFragment,
} from "~/meetingRecorder/TranscriptModal";
import TranscriptModalGenerating, { GeneratingSummaryPreview } from "~/meetingRecorder/TranscriptModalGenerating";
import TranscriptVideo, { CollapseButton } from "~/meetingRecorder/TranscriptVideo";
import {
	getTimestampFromLocation,
	useAutoRefetchMeetingRecordings,
	useIsPublic,
	useLongestMeetingRecording,
	useMeetingRecorderStatus,
} from "~/meetingRecorder/utils";
import { useSessionManager } from "~/meetingRecorder/Video/SessionManager";
import LoadingSpinner from "~/ui/LoadingSpinner";

import { streamStore } from "../utils";

import type { TranscriptModal_note$key } from "~/__generated__/TranscriptModal_note.graphql";
import type { TranscriptModal_viewer$key } from "~/__generated__/TranscriptModal_viewer.graphql";
import type { TranscriptVideo_videoPlaybackSettings$key } from "~/__generated__/TranscriptVideo_videoPlaybackSettings.graphql";

import styles from "../css/MeetingContainer.module.css";

type MeetingRecapProps = {
	noteKey: TranscriptModal_note$key;
	viewerKey: TranscriptModal_viewer$key;
	allowRecapDownload: boolean;
	videoPlaybackSettingsKey: TranscriptVideo_videoPlaybackSettings$key;
	containerRef?: React.RefObject<HTMLDivElement>;
	selectedRecordingId?: string;
	setSelectedRecordingId: (recordingId: string) => void;
	videoPlayerRef: React.RefObject<HTMLVideoElement>;
	chaptersRef: React.RefObject<ChaptersPanelRef>;
	onTimestampClick: (timestamp: number) => void;
};

export interface MeetingRecapRef {
	onLeaveRecapTab(): void;
	updateVideoState(playing: boolean, currentTime: number): void;
}

const MeetingRecap = React.forwardRef<MeetingRecapRef, MeetingRecapProps>(function MeetingRecap(
	{
		noteKey,
		viewerKey,
		allowRecapDownload,
		videoPlaybackSettingsKey,
		containerRef,
		selectedRecordingId,
		setSelectedRecordingId,
		videoPlayerRef: videoRef,
		chaptersRef,
		onTimestampClick,
	}: MeetingRecapProps,
	ref,
) {
	const note = useFragment<TranscriptModal_note$key>(transcriptNoteFragment, noteKey);
	const viewer = useFragment<TranscriptModal_viewer$key>(transcriptModalViewerFragment, viewerKey);
	const location = useLocation();

	const isPublic = useIsPublic();
	useAutoRefetchMeetingRecordings(isPublic ? null : note ? note : null);

	const contentRef = React.useRef<TranscriptModalContentRef>(null);

	// Ideally we would call this in NoteCard when initialising the selectedRecordingId state,
	// but it's a class component so we can't use the nice hook
	const longestMeetingRecording = useLongestMeetingRecording(note?.meetingRecordings || null);
	React.useEffect(() => {
		// selectedRecordingId doesn't get refreshed if we delete the selected recording without refreshing the page.
		// If the selected recording no longer exists in the note's meeting recordings but has been replaced with another,
		// then we select that one.
		if (
			longestMeetingRecording &&
			(!selectedRecordingId ||
				(selectedRecordingId &&
					!note.meetingRecordings?.find(recording => recording.id === selectedRecordingId) &&
					note.meetingRecordings &&
					note.meetingRecordings.length == 1))
		) {
			setSelectedRecordingId(longestMeetingRecording.id);
		} else if (selectedRecordingId) {
			setSelectedRecordingId(selectedRecordingId);
		}
	}, [longestMeetingRecording, note.meetingRecordings, selectedRecordingId, setSelectedRecordingId]);

	const [duration, setDuration] = React.useState<number | null>(null);
	const [thumbnailTrack, setThumbnailTrack] = React.useState<TextTrack | null>(null);
	const [mode, setMode] = React.useState<TranscriptMode>("default");
	const selectedRecording = note?.meetingRecordings?.find(recording => recording.id === selectedRecordingId);
	const activeClip = null;

	const [isNarrow, setIsNarrow] = React.useState(false);
	const { ref: wrapperRef } = useResizeObserver<HTMLDivElement>({
		onResize: useMemo(
			() =>
				throttle(({ width }) => {
					if (!width) return;

					setIsNarrow(width < 792); // 2 columns of 380 + 2 paddings of 16px
				}, 200),
			[],
		),
	});

	// We use localStorage to store the generating state of the recording in case
	// the page is refreshed or the user switches another part of the recording
	// while we are in between "CALL_ENDED" and "RECORDING_DONE" states.
	// We can't just rely on meetingRecordedVideoUrl and activeSummary because if they
	// don't exist we assume the recording has been deleted and we show the deletion state.
	const recorderStatus = useMeetingRecorderStatus(isPublic ? null : note);
	const [isGenerating, setIsGenerating] = React.useState(false);
	const recordingGeneratingCacheKey = `${selectedRecordingId}-generating`;
	React.useEffect(() => {
		const websocketStatus = recorderStatus.status;
		const cachedGenerating = streamStore.get(recordingGeneratingCacheKey);

		let generating = false;

		const videoAndSummaryExist = Boolean(
			selectedRecording?.meetingRecordedVideoUrl && selectedRecording?.activeSummary,
		);

		if (!websocketStatus) {
			// We don't have data from the websocket, but we might have cached data
			generating = !videoAndSummaryExist && Boolean(cachedGenerating);
		} else if (["RECORDING_DONE", "TRANSCRIPTION_DONE"].includes(websocketStatus)) {
			// When recording/transcription is done, show generating state if video + summary do not exist yet
			generating = !videoAndSummaryExist;
		} else if (["CALL_ENDED", "KICKED_OUT"].includes(websocketStatus)) {
			// When the call has just ended, show generating state
			generating = true;
		}

		if (generating) {
			streamStore.setExpire(recordingGeneratingCacheKey, moment(), moment().add(30, "minutes"));
		} else if (cachedGenerating) {
			streamStore.remove(recordingGeneratingCacheKey);
		}
		setIsGenerating(generating);
	}, [
		recorderStatus.status,
		recordingGeneratingCacheKey,
		selectedRecording?.activeSummary,
		selectedRecording?.meetingRecordedVideoUrl,
	]);

	const { analyticsFingerprint } = useContext(BillingContext);

	React.useEffect(() => {
		if (selectedRecording) {
			dispatchAnalyticsTrack("[AI Copilot] Recap Viewed", {
				fingerprint: analyticsFingerprint,
				source: "Note",
				/* eslint-disable camelcase */
				note_id: note?.id,
				meeting_recording_summary_id: selectedRecording.activeSummary?.id,
				has_external_attendees: selectedRecording.hasExternalAttendees,
				/* eslint-enable camelcase */
			});
		}
	}, [analyticsFingerprint, note?.id, selectedRecording]);

	const onVideoTimeUpdate = React.useCallback(() => {
		if (!videoRef.current || !contentRef.current) return;

		const { currentTime } = videoRef.current;
		const { transcriptRef, chaptersRef } = contentRef.current;

		transcriptRef.current?.handleTimeUpdate(currentTime);
		chaptersRef.current?.handleTimeUpdate(currentTime);
	}, [contentRef, videoRef]);

	const onVideoLoadedMetadata = React.useCallback(() => {
		if (!videoRef.current) return;
		if (isNaN(videoRef.current.duration)) return;

		const textTracks = videoRef.current.textTracks;
		for (let i = 0; i < textTracks.length; i += 1) {
			const track = textTracks[i];
			if (track.kind === "metadata" && track.label === "thumbnails") {
				setThumbnailTrack(track);
				break;
			}
		}

		setDuration(videoRef.current.duration);
	}, [setDuration, videoRef]);

	const onClipSelect = React.useCallback(
		(clipId: string) => {
			if (videoRef.current && !videoRef.current.paused) {
				// If the recording video is currently playing, pause it, so user can play the clip video
				videoRef.current.pause();
			}

			openClipModal(clipId);
		},
		[videoRef],
	);

	const [playedRecordings, setPlayedRecordings] = React.useState<string[]>([]);

	const onPlayVideo = React.useCallback(() => {
		if (!selectedRecordingId || playedRecordings.includes(selectedRecordingId)) return;

		setPlayedRecordings(prev => [...prev, selectedRecordingId]);
	}, [selectedRecordingId, playedRecordings]);

	const tabs = React.useMemo(() => {
		return note?.meetingRecordings
			?.filter(
				(recording): recording is typeof recording & { meetingCallStart: string; meetingCallEnd: string } =>
					!!recording.meetingCallStart && !!recording.meetingCallEnd,
			)
			.map((recording, i) => {
				return {
					label: `Part ${i + 1}`,
					value: recording.id,
				};
			});
	}, [note?.meetingRecordings]);

	const recordingOrClip = React.useMemo(() => activeClip ?? selectedRecording, [selectedRecording]);
	const onToggleMiniPlayer = React.useCallback(
		(clickEvent?: React.MouseEvent<HTMLElement>) => {
			if (!recordingOrClip) return;
			openTranscriptModal({
				recordingId: recordingOrClip.id,
				timestamp: videoRef.current?.currentTime,
				openedFromMiniplayerClick: Boolean(clickEvent),
				videoOriginRefs: {
					containerRef,
					videoRef,
				},
			});
			// Open the transcript modal passing some arguments that tells to go directly to the mini player
			// And use the current videoRef state
		},
		[recordingOrClip, videoRef, containerRef],
	);

	const updateVideoState = React.useCallback(
		(playing: boolean, time: number) => {
			if (!videoRef.current) return;
			videoRef.current.currentTime = time;
			videoRef.current.style.display = "initial";
			if (playing) {
				videoRef.current.play();
			}
		},
		[videoRef],
	);

	React.useImperativeHandle(
		ref,
		() => ({
			onLeaveRecapTab() {
				if (!videoRef.current || videoRef.current.paused) return;
				onToggleMiniPlayer();
			},
			updateVideoState,
		}),
		[videoRef, onToggleMiniPlayer, updateVideoState],
	);

	React.useEffect(() => {
		if (!videoRef.current) return;
		window.dispatchEvent(
			new CustomEvent(RECORDING_VIDEO_PLAYER_READY, {
				detail: { meetingRecordingId: recordingOrClip?.id, updateVideoState, videoRef, containerRef },
			}),
		);
	}, [updateVideoState, recordingOrClip, videoRef, containerRef]);

	const onVideoStartsPlaying = React.useCallback(() => {
		window.dispatchEvent(
			new CustomEvent(RECORDING_VIDEO_START_PLAYING, {
				detail: { meetingRecordingId: recordingOrClip?.id },
			}),
		);
	}, [recordingOrClip]);
	React.useEffect(() => {
		if (!videoRef.current) return;
		videoRef.current.addEventListener("play", onVideoStartsPlaying);
		const currentRef = videoRef.current;
		return () => {
			if (!currentRef) return;
			currentRef.removeEventListener("play", onVideoStartsPlaying);
		};
	}, [videoRef, onVideoStartsPlaying]);

	const openClipTrimmer = React.useCallback(() => {
		if (videoRef.current && !videoRef.current.paused) {
			// If the recording video is currently playing, pause it, so user can play the clip video
			videoRef.current.pause();
		}

		const videoTime = videoRef.current?.currentTime;
		contentRef.current?.openClipCutModal(videoTime);
	}, [videoRef]);

	const initialVideoTimestamp = getTimestampFromLocation(location);

	const [videoCollapsed, setVideoCollapsed] = React.useState(false);
	const toggleVideoCollapsed = React.useCallback(() => setVideoCollapsed(!videoCollapsed), [videoCollapsed]);

	useSessionManager(videoRef.current, selectedRecording?.id ?? null, null);

	if (isGenerating) {
		return (
			<div ref={wrapperRef}>
				<TranscriptModalGenerating isNarrow={isNarrow}>
					<GeneratingSummaryPreview />
				</TranscriptModalGenerating>
			</div>
		);
	}

	// Super unlikely to run into this case, but in case something is cached or not refetched correctly, we should show something
	if (!selectedRecording) {
		return (
			<div className={styles.errorState}>
				<FontAwesomeIcon icon={faSparkles} /> <b>There was an issue displaying the selected recording.</b> This may be
				fixed by refreshing the page.
			</div>
		);
	}

	return (
		<div ref={wrapperRef}>
			<TranscriptModalContent
				fromNote
				forceIsNarrow={isNarrow}
				note={note}
				highlightsNoteKey={note}
				statsNoteKey={note}
				viewer={viewer}
				ref={contentRef}
				videoRef={videoRef}
				chaptersRef={chaptersRef}
				onTimestampClick={onTimestampClick}
				meetingRecording={selectedRecording}
				videoPlaybackSettings={videoPlaybackSettingsKey}
				duration={duration}
				thumbnailTrack={thumbnailTrack}
				mode={mode}
				setMode={setMode}
				onClipSelect={onClipSelect}
				initialTab={initialVideoTimestamp != null ? "Transcript" : undefined}
				parts={tabs}
				setSelectedRecordingId={setSelectedRecordingId}
				allowRecapDownload={allowRecapDownload}
			>
				{recordingOrClip && (
					<>
						<div key="container-el" className={styles.playerContainer}>
							<div className={styles.miniPlayerMessage}>Video is in Miniplayer mode</div>
							<TranscriptVideo
								key={recordingOrClip.id}
								ref={videoRef}
								onTimeUpdate={onVideoTimeUpdate}
								onLoadedMetadata={onVideoLoadedMetadata}
								meetingRecording={recordingOrClip}
								videoPlaybackSettings={videoPlaybackSettingsKey}
								thumbnailTrack={thumbnailTrack}
								onPlay={onPlayVideo}
								tabs={tabs}
								setSelectedRecordingId={setSelectedRecordingId}
								isMiniPlayerActive={false}
								toggleMiniPlayer={onToggleMiniPlayer}
								hideClipButton={isNarrow}
								initialVideoTimestamp={initialVideoTimestamp}
								allowRecapDownload={allowRecapDownload}
								isCollapsed={videoCollapsed}
								setIsCollapsed={setVideoCollapsed}
								inClippingMode={false}
								openClipTrimmer={openClipTrimmer}
							>
								<track label="Subtitles" kind="subtitles" src={recordingOrClip.subtitlesVttUrl} />
								{recordingOrClip.thumbnailVttUrl && (
									<track default label="thumbnails" kind="metadata" src={recordingOrClip.thumbnailVttUrl} />
								)}
							</TranscriptVideo>
						</div>
						<CollapseButton
							isCollapsed={videoCollapsed}
							onClick={toggleVideoCollapsed}
							mediaType={recordingOrClip.mediaType}
						/>
					</>
				)}
			</TranscriptModalContent>
		</div>
	);
});

function RecapLoader() {
	return (
		<div className={styles.loaderWrapper}>
			<LoadingSpinner inline dimmer={false} size="large" />
		</div>
	);
}

export default withForwardedRefSuspense(<RecapLoader />, MeetingRecap);
